import { useId } from 'react'
import { insightOpportunityCopy } from '@testfeed/product-language'
import type { ResearchCard, StoreResearchCards } from '@testfeed/shopify-contracts'

const copy = insightOpportunityCopy.readingPreview
export type ResearchReadingCard = Pick<
  ResearchCard,
  'caseId' | 'title' | 'summary' | 'question' | 'limitation' | 'source'
>

export function ResearchReadingNotice({ state }: { state: StoreResearchCards['state'] }) {
  const messages = {
    ready: copy.empty,
    no_match: copy.empty,
    not_started: copy.empty,
    restricted: copy.restricted,
    pending: copy.pending,
    failed: copy.failed,
    insufficient_context: copy.insufficientContext,
    budget_deferred: copy.deferred,
  }
  return (
    <s-section heading={copy.staticHeading}>
      <s-paragraph color="subdued">{messages[state]}</s-paragraph>
    </s-section>
  )
}

/** Saved external examples. No generation or study action is available here. */
export function ResearchReadingSection({
  examples,
  treatment = 'conversation',
  intro = copy.staticIntro,
  onDismiss,
  busy = false,
}: {
  examples: ResearchReadingCard[]
  treatment?: 'conversation' | 'editorial'
  intro?: string
  onDismiss?: (caseId: string) => void
  busy?: boolean
}) {
  let empty = null
  if (!examples.length) empty = <s-paragraph color="subdued">{copy.empty}</s-paragraph>
  return (
    <s-query-container>
      <s-section heading={copy.staticHeading}>
        <s-icon slot="graphic" type="lightbulb" />
        <s-stack gap="base">
          <s-paragraph color="subdued">{intro}</s-paragraph>
          {empty}
          {examples.slice(0, 3).map((example) => (
            <ReadingRow
              key={example.caseId}
              example={example}
              treatment={treatment}
              onDismiss={onDismiss}
              busy={busy}
            />
          ))}
        </s-stack>
      </s-section>
    </s-query-container>
  )
}

function ReadingRow({
  example,
  treatment,
  onDismiss,
  busy,
}: {
  example: ResearchReadingCard
  treatment: 'conversation' | 'editorial'
  onDismiss?: (caseId: string) => void
  busy: boolean
}) {
  const id = useId()
  let dismiss = null
  if (onDismiss)
    dismiss = (
      <s-button variant="tertiary" disabled={busy} onClick={() => onDismiss(example.caseId)}>
        {copy.dismiss}
      </s-button>
    )
  let question = (
    <s-grid gridTemplateColumns="auto 1fr" gap="small" alignItems="start">
      <s-icon type="chat" />
      <s-text fontWeight="semibold">{example.question}</s-text>
    </s-grid>
  )
  if (treatment === 'editorial')
    question = (
      <s-grid gridTemplateColumns="auto 1fr" gap="small" alignItems="start">
        <span aria-hidden="true">
          <s-heading fontSize="large-400" accessibilityRole="presentation">
            “
          </s-heading>
        </span>
        <s-stack gap="small-200">
          <s-text color="subdued">{copy.worthAsking}</s-text>
          <s-text fontWeight="semibold">{example.question}</s-text>
        </s-stack>
      </s-grid>
    )
  return (
    <s-stack gap="base">
      <s-divider />
      <s-grid gridTemplateColumns="@container (inline-size <= 700px) 1fr, 1fr 1fr" gap="large" alignItems="center">
        <s-stack gap="small-200">
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-heading>{example.title}</s-heading>
            <s-button
              variant="tertiary"
              icon="info"
              commandFor={id}
              accessibilityLabel={copy.aboutInsight(example.title)}
            />
          </s-stack>
          <s-paragraph>{example.summary}</s-paragraph>
        </s-stack>
        {question}
      </s-grid>
      <s-popover id={id} maxInlineSize="340px">
        <s-box padding="base">
          <s-stack gap="small">
            <s-text fontWeight="semibold">{copy.context}</s-text>
            <s-paragraph>{example.limitation}</s-paragraph>
            {example.source.map((source) => (
              <s-stack key={source.url} gap="small-200">
                <s-text color="subdued">{source.publisher}</s-text>
                <s-paragraph color="subdued">{source.title}</s-paragraph>
              </s-stack>
            ))}
            {dismiss}
          </s-stack>
        </s-box>
      </s-popover>
    </s-stack>
  )
}
